import React, { Ref, forwardRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { chatApi } from 'api/chat';
import { ProgramRole } from 'types/Membership';
import { Dialog } from 'components/_new/Dialog';
import { Spinner } from 'components/_new/Spinner';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { Button } from 'components/_new/Button';
import { classNames } from 'primereact/utils';
import { ChatMessage, ChatThread, ChatThreadWithMessage } from 'types/Chat';
import { useWindowSize } from '@uidotdev/usehooks';
import { useChat } from './useChat';
import { ChatConversation } from './ChatConversation';
import { useChatThreadSubscriber } from './useChatSubscrber';
import { InputText } from '../_new/InputText';
import { useChatContext } from './ChatContextProvider';
import './chat.scss';

// @ts-ignore
// eslint-disable-next-line react/prop-types
const SearchFilter = ({ value, onChange, placeholder }) => {
	const { t } = useTranslation();

	return (
		<div className="relative w-full mb-2">
			<InputText
				value={value}
				onChange={onChange}
				placeholder={placeholder || t('programPanel.application.searchFilterOptionPlaceholder')}
				className="w-full"
				style={{ paddingLeft: '2rem' }}
				search
			/>
		</div>
	);
};

type ChatThreadListElement = {
	readThread: (programMembershipId: number) => void;
	updateThreadLastMessage: (message: ChatMessage) => void;
};

type ChatThreadListProps = {
	onThreadMissing?: (threadId: number) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChatThreadList = forwardRef((props: ChatThreadListProps, ref: Ref<ChatThreadListElement>) => {
	const { onThreadMissing } = props;
	const { width: windowWidth } = useWindowSize();
	const isMediumWindowSize = windowWidth ? windowWidth > 786 : false;

	const { t } = useTranslation();

	const { currentProgramMembershipId } = useChatContext();
	const {
		openDialog,
		currentThread,
		queryUniqueParam,
		isInit,
		dialogProps: { visible },
	} = useChat();

	const [chatThreads, setChatThreads] = useState<ChatThreadWithMessage[]>([]);
	const [searchTerm, setSearchTerm] = useState('');

	const { isLoading: chatThreadsLoading, isSuccess: chatThreadsSuccess } = useQuery(
		['chat-threads', queryUniqueParam],
		() => chatApi.getThreads(currentProgramMembershipId!),
		{
			enabled: Boolean(currentProgramMembershipId),
			onSuccess: (data) => {
				setChatThreads(data);
				if (
					currentThread &&
					!data
						.map(({ interlocutorProgramMembershipId }) => interlocutorProgramMembershipId)
						.includes(currentThread)
				) {
					onThreadMissing?.(currentThread);
				}
			},
		}
	);

	const [startAction, setStartAction] = useState(false);
	useLayoutEffect(() => {
		if (
			visible &&
			chatThreads &&
			chatThreads.length > 0 &&
			!currentThread &&
			isMediumWindowSize &&
			isInit &&
			!startAction
		) {
			openDialog(chatThreads[0].interlocutorProgramMembershipId);
			setStartAction(true);
		}
	}, [visible, chatThreads, currentThread, isMediumWindowSize, openDialog, isInit, startAction]);

	const sortThreadsByLastMessage = (
		{ lastMessageAt: aDateAt }: ChatThread,
		{ lastMessageAt: bDateAt }: ChatThread
	) => (new Date(aDateAt).getTime() <= new Date(bDateAt).getTime() ? 1 : -1);

	const handeUpdateThreadList = useCallback((data: ChatThreadWithMessage) => {
		setChatThreads((prev) => {
			// update exists thread
			if (prev.findIndex((thread) => thread.id === data.id) !== -1) {
				return prev
					.map((thread) => (thread.id === data.id ? { ...thread, ...data } : thread))
					.sort(sortThreadsByLastMessage);
			}
			// add new thread and sort it
			return [...prev, data].sort(sortThreadsByLastMessage);
		});
	}, []);

	const handeUpdateThreadListEvent = ({ detail }: CustomEvent<ChatThreadWithMessage>) =>
		handeUpdateThreadList(detail);
	useChatThreadSubscriber({ onChatThread: handeUpdateThreadListEvent, onChatThreadSelf: handeUpdateThreadListEvent });

	// Handle search input changes
	const handleSearchChange = (e: { target: { value: React.SetStateAction<string> } }) => {
		setSearchTerm(e.target.value);
	};

	// Filter threads based on search term
	const filteredThreads = chatThreads.filter((thread) => {
		if (!searchTerm) return true;
		const userName = userDisplayName(thread.interlocutorProgramMembership.user).toLowerCase();
		return userName.includes(searchTerm.toLowerCase());
	});

	const renderLastMessage = (options: { lastMessage: ChatMessage | null; hasNewMessage: boolean }) => {
		const { lastMessage, hasNewMessage } = options;
		if (lastMessage) {
			const { authorProgramMembershipId, message } = lastMessage;
			const lastMessageIsYou = Number(authorProgramMembershipId) === currentProgramMembershipId!;

			return (
				<div
					className={classNames('text-overflow text-xs', {
						'font-bold': hasNewMessage,
					})}
					style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
				>
					{`${lastMessageIsYou ? 'Ty: ' : ''}${message}`}
				</div>
			);
		}
		return '-';
	};

	const renderThreads = () =>
		(filteredThreads || []).map((thread) => {
			const { interlocutorProgramMembership, lastMessage } = thread;

			const isActive = currentThread === interlocutorProgramMembership.id;
			return (
				<li key={thread.id}>
					<Button
						variant={isActive ? 'primary' : 'primary-outlined'}
						label={userDisplayName(interlocutorProgramMembership.user)}
						className="w-full py-1 px-2"
						onClick={() => {
							openDialog(interlocutorProgramMembership.id);
						}}
					>
						<div className="w-full flex flex-row gap-2 align-items-center">
							<Avatar
								id={interlocutorProgramMembership.user.id}
								src={interlocutorProgramMembership.user.avatar}
								size="lg"
								name={userDisplayName(interlocutorProgramMembership.user, null)}
							/>
							<div className="flex flex-column gap-0 text-left overflow-hidden">
								<div className="flex flex-column gap-0">
									<span>{userDisplayName(interlocutorProgramMembership.user)}</span>
									{/* {interlocutorProgramMembership.role === 'program admin' && (
										<span className="bold text-xxs line-xxs text-rose">{t('misc.organizer')}</span>
									)} */}
								</div>

								<div>
									{renderLastMessage({
										lastMessage,
										hasNewMessage: thread.hasNewMessage,
									})}
									<div className="text-gray text-xxs line-xs">
										{dayjs(thread.lastMessageAt).fromNow()}
									</div>
								</div>
							</div>
						</div>
					</Button>
				</li>
			);
		});

	// Display search input if there are more than 5 threads
	const showSearch = chatThreads.length > 5;

	return (
		<div>
			{chatThreadsLoading ? (
				<Spinner />
			) : (
				<div>
					{chatThreadsSuccess && !chatThreads.length ? (
						<p className="w-full text-center text-gray">{t('chat.noConversations')}</p>
					) : (
						<>
							{showSearch && (
								<SearchFilter
									value={searchTerm}
									onChange={handleSearchChange}
									placeholder={t('programPanel.application.searchFilterOptionPlaceholder')}
								/>
							)}
							<ul className="flex flex-column gap-1 list-none">{renderThreads()}</ul>
						</>
					)}
				</div>
			)}
		</div>
	);
});

// eslint-disable-next-line @typescript-eslint/ban-types
type ChatMembersListProps = {};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChatMembersList = (props: ChatMembersListProps) => {
	const { t } = useTranslation();
	const { currentProgramMembershipId } = useChatContext();
	const { currentThread, openDialog, queryUniqueParam } = useChat();

	const {
		data: chatMembers,
		isLoading: chatMembersLoading,
		isSuccess: chatMembersSuccess,
	} = useQuery(
		['chat-unthread-members', queryUniqueParam],
		() => chatApi.getUnthreadMembers(currentProgramMembershipId!),
		{
			enabled: Boolean(currentProgramMembershipId),
		}
	);

	const [searchTerm, setSearchTerm] = useState('');

	// Handle search input changes
	const handleSearchChange = (e: { target: { value: React.SetStateAction<string> } }) => {
		setSearchTerm(e.target.value);
	};

	// Filter members based on search term
	const filteredMembers = chatMembers
		? chatMembers.filter((member) => {
				if (!searchTerm) return true;
				const userName = userDisplayName(member.user).toLowerCase();
				return userName.includes(searchTerm.toLowerCase());
			})
		: [];

	const renderMembers = () =>
		(filteredMembers || []).map((programMembership) => {
			const isActive = currentThread === programMembership.id;
			return (
				<li key={programMembership.id}>
					<Button
						variant={isActive ? 'primary' : 'primary-outlined'}
						label={userDisplayName(programMembership.user)}
						className="w-full py-1 px-2"
						onClick={() => {
							openDialog(programMembership.id);
						}}
					>
						<div className="w-full flex flex-row gap-2 align-items-center">
							<Avatar
								id={programMembership.user.id}
								src={programMembership.user.avatar}
								size="lg"
								name={userDisplayName(programMembership.user, null)}
							/>
							<div className="flex flex-column gap-0 text-left overflow-hidden">
								<div>{userDisplayName(programMembership.user)}</div>
								{[ProgramRole.PROGRAM_ADMIN, ProgramRole.PAIRS_COORDINATOR].includes(
									programMembership.role
								) && <span className="bold text-xxs line-xxs text-rose">{t('misc.organizer')}</span>}
							</div>
						</div>
					</Button>
				</li>
			);
		});

	// Display search input if there are more than 5 members
	const showSearch = chatMembers && chatMembers.length > 5;

	return (
		<div>
			{chatMembersLoading ? (
				<Spinner />
			) : (
				<div>
					{chatMembersSuccess && !chatMembers.length ? (
						<p className="w-full text-center text-gray">{t('chat.noUnthreads')}</p>
					) : (
						<>
							{showSearch && (
								<SearchFilter
									value={searchTerm}
									onChange={handleSearchChange}
									placeholder={t('actions.search')}
								/>
							)}
							<ul className="flex flex-column gap-1 list-none">{renderMembers()}</ul>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export const ChatDialog = () => {
	const { t } = useTranslation();

	const { resetDialog, currentThread, dialogProps } = useChat();

	const [newChatMode, setNewChatMode] = useState(false);

	const handleNewChatClick = useCallback(() => {
		resetDialog();
		setNewChatMode((prev) => !prev);
	}, [resetDialog]);

	useEffect(() => {
		if (dialogProps.visible) {
			setNewChatMode(false);
		}
	}, [dialogProps.visible]);

	// const [interlocutorProgramMembership, setInterlocutorProgramMembership] = useState<ProgramMembership | null>(null);
	// const handleThreadLoad = useCallback((interlocutorProgramMembership: ProgramMembership) => {
	// 	// setInterlocutorProgramMembership(interlocutorProgramMembership);
	// 	setNewChatMode(true);
	// }, []);

	const handleThreadMissing = () => {
		setNewChatMode(true);
	};

	const handleBackClick = useCallback(() => {
		resetDialog();
	}, [resetDialog]);

	return (
		<Dialog
			title={t('chat.title')}
			size="lg"
			maxHeight
			{...dialogProps}
			bodyClassName="overflow-y-hidden overflow-x-hidden p-0"
		>
			<div className="flex flex-row gap-2 h-full min-w-0 overflow-hidden">
				<div
					className={classNames(
						' gap-3 px-2 py-2 pr-0 md:p-4 md:pr-2 overflow-y-scroll p-styled-scrollbar-purplishblue chat-thread-col',
						{
							'chat-active-thread': Boolean(currentThread),
						}
					)}
				>
					<Button
						onClick={handleNewChatClick}
						label={newChatMode ? t('chat.back') : t('chat.newChat')}
						variant="primary-outlined"
						variantSize="md"
						block
						responsiveClassName="hidden md:flex"
					/>
					<Button
						onClick={handleNewChatClick}
						label={newChatMode ? t('chat.back') : t('chat.newChatShort')}
						variant="primary-outlined"
						variantSize="md"
						block
						responsiveClassName="flex md:hidden"
					/>
					<div className="">
						<div className="mb-2 hidden md:block">
							{`${newChatMode ? t('chat.startNewConversation') : t('chat.currentThreads')}:`}
						</div>
						{newChatMode && <ChatMembersList />}
						{!newChatMode && <ChatThreadList onThreadMissing={handleThreadMissing} />}
					</div>
				</div>
				<div
					className={classNames(
						'flex-1 min-w-0 h-full w-full pr-2 py-2 pl-0 md:p-4 md:pl-2 overflow-y-hidden overflow-x-hidden',
						{
							'hidden md:flex': !currentThread,
						}
					)}
				>
					{currentThread ? (
						<ChatConversation threadId={currentThread} onBackClick={handleBackClick} />
					) : (
						<div className="flex-1 h-full flex flex-column justify-content-center align-items-center">
							<span>{t('chat.emptyScreenDesc')}</span>
						</div>
					)}
				</div>
			</div>
		</Dialog>
	);
};
